import { Component, inject, OnInit, signal } from '@angular/core';
import { GoogleSigninButtonDirective } from '@abacritt/angularx-social-login';
import { GoogleIdTokenService, GoogleLoginMode } from './google-id-token.service';

/**
 * The "Sign in with Google" button of the login and register pages (app#397). Popup mode renders
 * the Google Identity Services button as before; relay mode renders a plain button of the same
 * look that leaves for auth.fsn — no GIS script, so no "origin not allowed" on a preview host.
 * The resulting ID token reaches the page through `GoogleIdTokenService.signIns()`, not through
 * this component.
 */
@Component({
  selector: 'app-google-signin-button',
  imports: [GoogleSigninButtonDirective],
  template: `
    @if (mode() === 'popup') {
      <asl-google-signin-button locale="en" size="large" type="standard"></asl-google-signin-button>
    } @else if (mode() === 'relay') {
      <button
        type="button"
        class="google-relay-button"
        data-testid="google-relay-signin"
        [disabled]="busy()"
        (click)="startRelay()"
      >
        <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
          <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
          />
          <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
          />
          <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
          />
          <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
          />
        </svg>
        <span>Sign in with Google</span>
      </button>
    }
  `,
  styles: `
    :host {
      display: block;
    }
    .google-relay-button {
      display: inline-flex;
      width: 100%;
      justify-content: center;
      box-sizing: border-box;
      align-items: center;
      gap: 10px;
      height: 40px;
      padding: 0 12px;
      border: 1px solid #dadce0;
      border-radius: 4px;
      background: #fff;
      color: #3c4043;
      font-family: 'Google Sans', Roboto, arial, sans-serif;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.25px;
      cursor: pointer;
      white-space: nowrap;
    }
    .google-relay-button:hover {
      background: #f8faff;
      border-color: #d2e3fc;
    }
  `,
})
export class GoogleSigninButtonComponent implements OnInit {
  private readonly googleSignIn = inject(GoogleIdTokenService);
  readonly mode = signal<GoogleLoginMode | undefined>(undefined);
  /** Set on the first click and never cleared: the page is about to leave for auth.fsn. */
  readonly busy = signal(false);

  ngOnInit(): void {
    this.googleSignIn.mode().then(
      (mode) => this.mode.set(mode),
      (err) => console.warn('[google-relay] provider lookup failed', err?.status ?? err)
    );
  }

  startRelay(): void {
    if (this.busy()) {
      return;
    }
    this.busy.set(true);
    this.googleSignIn.startRelay().catch((err) => {
      console.warn('[google-relay] could not start the relay', err?.status ?? err);
      this.busy.set(false);
    });
  }
}
